import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Search, Mail, ShieldCheck, ShieldOff, RotateCcw, Power, PowerOff,
  KeyRound, Users, GitBranch, CheckSquare, Square, Trash2, Ban, BadgeDollarSign,
  Pencil, FileDown, Eye, Wallet, X, Send,
} from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";

const ACCENT = "#22c073";       // Maxlive88 green
const GOLD = "#d4af37";
const NAVY = "#0a1018";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function downloadFile(url, filename) {
  const token = localStorage.getItem("ss_token");
  fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then((r) => {
      if (!r.ok) throw new Error("Download failed");
      return r.blob();
    })
    .then((blob) => {
      const a = document.createElement("a");
      const u = URL.createObjectURL(blob);
      a.href = u; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    })
    .catch((e) => toast.error(e.message || "Download failed"));
}

export default function AffiliateAccounts() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [uplineFor, setUplineFor] = useState(null);
  const [balanceFor, setBalanceFor] = useState(null);
  const [promoFor, setPromoFor] = useState(null);
  const [removeFor, setRemoveFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const paged = usePagedRows(rows, 12, [q, showRemoved]);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/aff-accounts", { params: q.trim() ? { q: q.trim() } : {} });
      let r = data.rows || [];
      if (!showRemoved) r = r.filter((x) => (x.status || "active") !== "removed");
      setRows(r);
      setSelected(new Set());
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [q, showRemoved]);
  useEffect(() => { load(); }, [load]);

  const toggleSel = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.user_id)));
  };

  const bulk = async (action) => {
    if (selected.size === 0) return toast.error("Select at least one affiliate");
    const labels = { enable: "Enable", disable: "Disable", reset_password: "Reset Password & Email", resend_creds: "Resend Credentials" };
    if (!window.confirm(`${labels[action]} for ${selected.size} accounts?`)) return;
    try {
      const r = await api.post("/admin/aff-accounts/bulk", { user_ids: Array.from(selected), action });
      toast.success(`Done: ${r.data?.processed || 0} processed`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setUpline = async (uplineUserId) => {
    if (!uplineFor) return;
    try {
      await api.post("/admin/aff-upline", { user_id: uplineFor.user_id, upline_user_id: uplineUserId || null });
      toast.success(uplineUserId ? "Upline linked" : "Upline cleared");
      setUplineFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const resetPw = async (userId) => {
    if (!window.confirm("Generate a new password and email it?")) return;
    try {
      const { data } = await api.post(`/admin/aff-accounts/${userId}/reset-password`);
      if (data.email_sent) toast.success("New password emailed");
      else if (data.password) toast.success(`Password (email failed): ${data.password}`);
      else toast.success("Reset done");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const resend = async (userId) => {
    if (!window.confirm("Resend credentials? This will generate a NEW password.")) return;
    try {
      const { data } = await api.post(`/admin/aff-accounts/${userId}/resend-credentials`);
      if (data.email_sent) toast.success("Credentials emailed");
      else if (data.password) toast.success(`Password (email failed): ${data.password}`);
      else toast.success("Resent");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleStatus = async (userId, current) => {
    const action = current === "active" ? "disable" : "enable";
    try {
      await api.post(`/admin/aff-accounts/${userId}/${action}`);
      toast.success(`Account ${action}d`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const togglePayoutBlock = async (r) => {
    const blocked = !r.payout_blocked;
    const reason = blocked ? (window.prompt("Reason (shown to affiliate):", "Risk review") || "") : "";
    if (blocked && reason === null) return;
    try {
      await api.post(`/admin/aff-accounts/${r.user_id}/payout-block`, { blocked, reason });
      toast.success(blocked ? "Payouts blocked" : "Payouts unblocked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reset2fa = async (r) => {
    if (!window.confirm(`Reset 2FA for ${r.username}? They'll need to reconfigure on next login.`)) return;
    try {
      await api.post(`/admin/aff-accounts/${r.user_id}/2fa-reset`);
      toast.success("2FA reset — affiliate must reconfigure");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeAccount = async () => {
    if (!removeFor) return;
    if (!removeFor.confirmText || removeFor.confirmText !== removeFor.username) {
      toast.error(`Type "${removeFor.username}" to confirm`);
      return;
    }
    try {
      await api.post(`/admin/aff-accounts/${removeFor.user_id}/remove`, { reason: removeFor.reason || "" });
      toast.success("Account permanently removed");
      setRemoveFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const adjustBalance = async () => {
    if (!balanceFor) return;
    const delta = parseFloat(balanceFor.delta);
    if (!delta || Number.isNaN(delta)) return toast.error("Enter a non-zero amount");
    try {
      const { data } = await api.post(`/admin/aff-accounts/${balanceFor.user_id}/balance-adjust`, {
        delta, reason: balanceFor.reason || "",
      });
      toast.success(`Wallet ${delta >= 0 ? "credited" : "debited"} ৳${Math.abs(delta).toFixed(2)} · New balance ৳${fmt(data.new_balance)}`);
      setBalanceFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setPromoCode = async () => {
    if (!promoFor) return;
    const code = (promoFor.code || "").trim().toUpperCase();
    if (!/^[A-Z0-9]{4,12}$/.test(code)) return toast.error("4-12 chars, A-Z / 0-9 only");
    try {
      const { data } = await api.patch(`/admin/aff-accounts/${promoFor.user_id}/promo-code`, { code });
      toast.success(`Promo code set: ${data.promo_code}`);
      setPromoFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const stats = {
    total: rows.length,
    active: rows.filter((r) => (r.status || "active") === "active").length,
    twoFA: rows.filter((r) => r.two_factor_enabled).length,
    walletTotal: rows.reduce((s, r) => s + Number(r.wallet_balance || 0), 0),
  };

  return (
    <div>
      <PageHeader title="Affiliate Panel Accounts" subtitle="Promo & Affiliate panel — premium account management with real-time wallet, payout-block, 2FA reset, balance adjust & PDF reports" />

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <AdminStat label="Total Accounts" value={stats.total} color={GOLD} icon={Users} />
        <AdminStat label="Active" value={stats.active} color={ACCENT} icon={Power} />
        <AdminStat label="2FA Enabled" value={stats.twoFA} color="#3b82f6" icon={ShieldCheck} />
        <AdminStat label="Wallet Total" value={`৳ ${fmt(stats.walletTotal)}`} color={GOLD} icon={Wallet} />
      </div>

      <div className="rounded-xl p-3 mb-3 flex items-center gap-2 flex-wrap" style={{
        background: `linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`,
        border: `1px solid ${ACCENT}3a`,
      }}>
        <div className="flex items-center gap-2 h-9 px-3 rounded-md flex-1 max-w-md" style={{ background: "rgba(0,0,0,0.32)", border: `1px solid ${ACCENT}30` }}>
          <Search size={13} style={{ color: ACCENT }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="username or email" className="bg-transparent outline-none text-[13px] flex-1 text-white" data-testid="aff-search-input" />
        </div>
        <button onClick={load} className="h-9 px-3 rounded-md text-[12px] inline-flex items-center gap-1.5 font-semibold" style={{
          background: `${ACCENT}1a`, border: `1px solid ${ACCENT}55`, color: ACCENT,
        }} data-testid="aff-refresh-btn">
          <RefreshCw size={12} /> Refresh
        </button>
        <label className="h-9 px-3 rounded-md text-[12px] inline-flex items-center gap-1.5 font-semibold cursor-pointer" style={{
          background: showRemoved ? `${GOLD}1a` : "rgba(255,255,255,0.04)", border: `1px solid ${showRemoved ? GOLD : "rgba(255,255,255,0.10)"}`, color: showRemoved ? GOLD : "#cbd5e1",
        }}>
          <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} className="hidden" />
          {showRemoved ? "Showing Removed" : "Hide Removed"}
        </label>
        <button onClick={() => downloadFile(`${apiBase}/api/admin/aff-reports/accounts.pdf`, "affiliate_accounts.pdf")} className="h-9 px-3 rounded-md text-[12px] inline-flex items-center gap-1.5 font-bold" style={{ background: `${GOLD}1f`, border: `1px solid ${GOLD}66`, color: GOLD }} data-testid="aff-pdf-accounts">
          <FileDown size={12} /> PDF · Accounts
        </button>
        <button onClick={() => downloadFile(`${apiBase}/api/admin/aff-reports/payouts.pdf?days=30`, "affiliate_payouts_30d.pdf")} className="h-9 px-3 rounded-md text-[12px] inline-flex items-center gap-1.5 font-bold" style={{ background: `${GOLD}1f`, border: `1px solid ${GOLD}66`, color: GOLD }} data-testid="aff-pdf-payouts">
          <FileDown size={12} /> PDF · Payouts
        </button>
        <button onClick={() => downloadFile(`${apiBase}/api/admin/aff-reports/commissions.pdf?days=90`, "affiliate_commissions_90d.pdf")} className="h-9 px-3 rounded-md text-[12px] inline-flex items-center gap-1.5 font-bold" style={{ background: `${GOLD}1f`, border: `1px solid ${GOLD}66`, color: GOLD }} data-testid="aff-pdf-commissions">
          <FileDown size={12} /> PDF · Commissions
        </button>
        <button
          onClick={async () => {
            if (!window.confirm("Run auto-commission credit now? This processes the previous 15-day period (idempotent).")) return;
            try { const { data } = await api.post("/admin/aff-commissions/run-auto-credit"); toast.success(`Done · ${data.created} credited (৳${fmt(data.credited_total)})`); load(); }
            catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
          }}
          className="h-9 px-3 rounded-md text-[12px] inline-flex items-center gap-1.5 font-bold"
          style={{ background: `${ACCENT}28`, border: `1px solid ${ACCENT}77`, color: ACCENT }}
          data-testid="run-auto-credit"
        >
          <Send size={12} /> Run Auto-Credit
        </button>
        {selected.size > 0 && (
          <>
            <div className="h-8 mx-2 w-px" style={{ background: `${ACCENT}55` }} />
            <span className="text-[12px] font-bold" style={{ color: ACCENT }}>{selected.size} selected</span>
            <button onClick={() => bulk("enable")} className="h-8 px-2.5 rounded-md text-[11px] font-bold" style={{ background: "rgba(16,185,129,0.16)", border: "1px solid rgba(16,185,129,0.45)", color: "#6ee7b7" }} data-testid="bulk-enable">Bulk Enable</button>
            <button onClick={() => bulk("disable")} className="h-8 px-2.5 rounded-md text-[11px] font-bold" style={{ background: "rgba(229,75,89,0.16)", border: "1px solid rgba(229,75,89,0.45)", color: "#fda5b1" }} data-testid="bulk-disable">Bulk Disable</button>
            <button onClick={() => bulk("reset_password")} className="h-8 px-2.5 rounded-md text-[11px] font-bold" style={{ background: `${GOLD}1f`, border: `1px solid ${GOLD}66`, color: GOLD }} data-testid="bulk-reset">Bulk Reset PW</button>
            <button onClick={() => bulk("resend_creds")} className="h-8 px-2.5 rounded-md text-[11px] font-bold" style={{ background: "rgba(59,130,246,0.16)", border: "1px solid rgba(59,130,246,0.45)", color: "#93c5fd" }} data-testid="bulk-resend">Bulk Resend</button>
          </>
        )}
      </div>

      <div className="rounded-xl p-3 overflow-x-auto text-white" style={{
        background: `linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`,
        border: `1px solid ${ACCENT}3a`,
      }}>
        {loading ? <div className="text-white/55 py-10 text-center">Loading…</div>
          : rows.length === 0 ? <div className="text-white/55 py-10 text-center">No accounts found</div>
          : (
            <table className="w-full text-sm min-w-[1180px] text-white" data-testid="aff-accounts-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest" style={{ color: `${ACCENT}cc` }}>
                  <th className="py-2 pr-2 w-7">
                    <button onClick={toggleAll} className="hover:opacity-80" style={{ color: ACCENT }}>
                      {selected.size === rows.length && rows.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
                    </button>
                  </th>
                  <th className="py-2 pr-2">Username</th>
                  <th className="py-2 pr-2">Email</th>
                  <th className="py-2 pr-2">Promo Code</th>
                  <th className="py-2 pr-2">Wallet ৳</th>
                  <th className="py-2 pr-2">Comm %</th>
                  <th className="py-2 pr-2">2FA</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2 pr-2">Payout</th>
                  <th className="py-2 pr-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.visible.map((r) => (
                  <tr key={r.id} className="hover:bg-emerald-500/5" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                    <td className="py-2.5 pr-2">
                      <button onClick={() => toggleSel(r.user_id)} className="hover:opacity-80" style={{ color: ACCENT }} data-testid={`sel-${r.user_id}`}>
                        {selected.has(r.user_id) ? <CheckSquare size={14} /> : <Square size={14} />}
                      </button>
                    </td>
                    <td className="py-2.5 pr-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10.5px] font-black shrink-0" style={{
                          background: `linear-gradient(135deg, ${GOLD}, ${ACCENT})`,
                          color: NAVY,
                        }}>{(r.username || "?").slice(0, 2).toUpperCase()}</div>
                        <div>
                          <div className="font-bold">{r.username}</div>
                          {r.must_change_password && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: `${GOLD}1f`, border: `1px solid ${GOLD}66`, color: GOLD }}>PWD CHG</span>}
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 pr-2 text-white/80 text-xs">{r.email || "—"}</td>
                    <td className="py-2.5 pr-2">
                      <div className="inline-flex items-center gap-1">
                        <span className="font-mono font-bold" style={{ color: GOLD }}>{r.promo_code || "—"}</span>
                        <button onClick={() => setPromoFor({ user_id: r.user_id, username: r.username, code: r.promo_code || "" })} title="Customize promo code" className="opacity-60 hover:opacity-100" data-testid={`set-promo-${r.user_id}`}>
                          <Pencil size={11} />
                        </button>
                      </div>
                    </td>
                    <td className="py-2.5 pr-2">
                      <div className="inline-flex items-center gap-1">
                        <span className="font-bold" style={{ color: ACCENT }}>৳ {fmt(r.wallet_balance)}</span>
                        <button onClick={() => setBalanceFor({ user_id: r.user_id, username: r.username, current: r.wallet_balance || 0, delta: "", reason: "" })} title="Adjust balance" className="opacity-60 hover:opacity-100" data-testid={`adjust-bal-${r.user_id}`}>
                          <BadgeDollarSign size={12} />
                        </button>
                      </div>
                    </td>
                    <td className="py-2.5 pr-2 font-bold" style={{ color: GOLD }}>{r.commission_pct_override != null ? `${r.commission_pct_override}%` : "30%"}</td>
                    <td className="py-2.5 pr-2">
                      <button onClick={() => reset2fa(r)} title={r.two_factor_enabled ? "2FA on (click to reset)" : "2FA off"} data-testid={`reset-2fa-${r.user_id}`}>
                        {r.two_factor_enabled ? <ShieldCheck size={14} className="text-emerald-300" /> : <ShieldOff size={14} className="text-white/30" />}
                      </button>
                    </td>
                    <td className="py-2.5 pr-2">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{
                        background: r.status === "removed" ? "rgba(239,68,68,0.18)" : r.status === "active" ? "rgba(34,192,115,0.18)" : "rgba(229,75,89,0.18)",
                        border: r.status === "removed" ? "1px solid rgba(239,68,68,0.5)" : r.status === "active" ? "1px solid rgba(34,192,115,0.5)" : "1px solid rgba(229,75,89,0.45)",
                        color: r.status === "removed" ? "#fca5a5" : r.status === "active" ? "#6ee7b7" : "#fda5b1",
                      }}>{(r.status || "active").toUpperCase()}</span>
                    </td>
                    <td className="py-2.5 pr-2">
                      <button onClick={() => togglePayoutBlock(r)} title={r.payout_blocked ? `Blocked: ${r.payout_block_reason || ""}` : "Allowed"} className="px-2 h-7 rounded-md text-[10.5px] font-bold inline-flex items-center gap-1" style={{
                        background: r.payout_blocked ? "rgba(239,68,68,0.16)" : "rgba(34,192,115,0.14)",
                        border: r.payout_blocked ? "1px solid rgba(239,68,68,0.45)" : "1px solid rgba(34,192,115,0.45)",
                        color: r.payout_blocked ? "#fca5a5" : "#6ee7b7",
                      }} data-testid={`payout-block-${r.user_id}`}>
                        <Ban size={11} /> {r.payout_blocked ? "Blocked" : "Allowed"}
                      </button>
                    </td>
                    <td className="py-2.5 pr-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        <button onClick={() => setDetailFor(r)} title="View players & drill-down" className="px-2 h-7 rounded-md text-xs font-bold inline-flex items-center gap-1" style={{ background: `${ACCENT}1f`, border: `1px solid ${ACCENT}55`, color: ACCENT }} data-testid={`view-${r.user_id}`}>
                          <Eye size={11} /> View
                        </button>
                        <button onClick={() => downloadFile(`${apiBase}/api/admin/aff-accounts/${r.user_id}/detail.pdf?period_days=30`, `affiliate_${r.username}.pdf`)} title="Download PDF" className="px-2 h-7 rounded-md text-xs font-bold inline-flex items-center gap-1" style={{ background: `${GOLD}1f`, border: `1px solid ${GOLD}55`, color: GOLD }} data-testid={`pdf-${r.user_id}`}>
                          <FileDown size={11} /> PDF
                        </button>
                        <button onClick={() => resetPw(r.user_id)} title="Reset password" className="px-2 h-7 rounded-md text-xs font-bold inline-flex items-center gap-1" style={{ background: `${GOLD}1a`, border: `1px solid ${GOLD}55`, color: GOLD }}><RotateCcw size={11} /></button>
                        <button onClick={() => resend(r.user_id)} title="Email credentials" className="px-2 h-7 rounded-md text-xs font-bold inline-flex items-center gap-1" style={{ background: "rgba(59,130,246,0.18)", border: "1px solid rgba(59,130,246,0.45)", color: "#93c5fd" }}><Mail size={11} /></button>
                        <button onClick={() => setUplineFor({ user_id: r.user_id, username: r.username, current: r.upline_user_id })} title="Set upline" className="px-2 h-7 rounded-md text-xs font-bold inline-flex items-center gap-1" style={{ background: "rgba(168,85,247,0.15)", border: "1px solid rgba(168,85,247,0.45)", color: "#d8b4fe" }}><GitBranch size={11} /></button>
                        {r.status !== "removed" && (
                          <button onClick={() => toggleStatus(r.user_id, r.status)} title={r.status === "active" ? "Disable" : "Enable"} className="px-2 h-7 rounded-md text-xs font-bold inline-flex items-center gap-1" style={{
                            background: r.status === "active" ? "rgba(229,75,89,0.16)" : "rgba(34,192,115,0.18)",
                            border: r.status === "active" ? "1px solid rgba(229,75,89,0.45)" : "1px solid rgba(34,192,115,0.45)",
                            color: r.status === "active" ? "#fda5b1" : "#6ee7b7",
                          }}>{r.status === "active" ? <PowerOff size={11} /> : <Power size={11} />}</button>
                        )}
                        {r.status !== "removed" && (
                          <button onClick={() => setRemoveFor({ user_id: r.user_id, username: r.username, reason: "", confirmText: "" })} title="Remove account permanently" className="px-2 h-7 rounded-md text-xs font-bold inline-flex items-center gap-1" style={{ background: "rgba(239,68,68,0.16)", border: "1px solid rgba(239,68,68,0.45)", color: "#fca5a5" }} data-testid={`remove-${r.user_id}`}>
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        <Pagination total={paged.totalRows} page={paged.page} pageSize={paged.pageSize} onChange={paged.setPage} testIdPrefix="aff-accounts-pg" />
      </div>

      {/* Upline modal */}
      {uplineFor && (
        <Modal onClose={() => setUplineFor(null)}>
          <ModalHead eyebrow="UPLINE / SUB-AFFILIATE" title={<>Link upline for <span style={{ color: ACCENT }}>{uplineFor.username}</span></>} />
          <p className="text-white/55 text-[13px] mb-3">Pick an existing affiliate as upline. Override commission will flow upward when this affiliate earns.</p>
          <select className="ap-input w-full" defaultValue={uplineFor.current || ""} onChange={(e) => setUplineFor({ ...uplineFor, picked: e.target.value })}>
            <option value="">— No upline —</option>
            {rows.filter((r) => r.user_id !== uplineFor.user_id).map((r) => (
              <option key={r.user_id} value={r.user_id}>{r.username}</option>
            ))}
          </select>
          <ModalActions onCancel={() => setUplineFor(null)} onSave={() => setUpline(uplineFor.picked != null ? uplineFor.picked : (uplineFor.current || ""))} />
        </Modal>
      )}

      {/* Balance adjust modal */}
      {balanceFor && (
        <Modal onClose={() => setBalanceFor(null)}>
          <ModalHead eyebrow="WALLET ADJUSTMENT" title={<>Adjust balance for <span style={{ color: ACCENT }}>{balanceFor.username}</span></>} />
          <div className="text-[13px] text-white/65 mb-3">Current wallet: <b className="text-white">৳ {fmt(balanceFor.current)}</b>. Use negative number to debit.</div>
          <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold">Delta (BDT)</label>
          <input type="number" step="0.01" autoFocus className="ap-input w-full mb-3" value={balanceFor.delta} onChange={(e) => setBalanceFor({ ...balanceFor, delta: e.target.value })} placeholder="e.g. 500 or -200" data-testid="balance-delta" />
          <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold">Reason (shown to affiliate)</label>
          <input className="ap-input w-full" value={balanceFor.reason} onChange={(e) => setBalanceFor({ ...balanceFor, reason: e.target.value })} placeholder="Bonus / Correction / Refund" data-testid="balance-reason" />
          <ModalActions onCancel={() => setBalanceFor(null)} onSave={adjustBalance} saveLabel="Apply" saveTestId="balance-apply" />
        </Modal>
      )}

      {/* Promo code modal */}
      {promoFor && (
        <Modal onClose={() => setPromoFor(null)}>
          <ModalHead eyebrow="CUSTOMIZE PROMO CODE" title={<>Set code for <span style={{ color: ACCENT }}>{promoFor.username}</span></>} />
          <p className="text-white/55 text-[13px] mb-3">4-12 characters · uppercase letters & digits only.</p>
          <input autoFocus className="ap-input w-full font-mono uppercase tracking-widest text-center text-[18px]" value={promoFor.code} onChange={(e) => setPromoFor({ ...promoFor, code: e.target.value.toUpperCase() })} placeholder="MAXAFF99" data-testid="promo-code-input" />
          <ModalActions onCancel={() => setPromoFor(null)} onSave={setPromoCode} saveLabel="Save Code" saveTestId="promo-code-save" />
        </Modal>
      )}

      {/* Remove account modal */}
      {removeFor && (
        <Modal onClose={() => setRemoveFor(null)} accent="#ef4444">
          <ModalHead eyebrow="PERMANENT REMOVAL" title={<><span style={{ color: "#fca5a5" }}>⚠ Remove </span> <span style={{ color: GOLD }}>{removeFor.username}</span></>} accent="#ef4444" />
          <div className="text-[13px] text-white/65 mb-3">
            This will <b className="text-red-300">permanently revoke access</b> for this affiliate. The account will no longer be able to log in. Data is preserved for compliance/audit, and the affiliate will receive a final notice via email.
          </div>
          <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold">Reason</label>
          <input className="ap-input w-full mb-3" value={removeFor.reason} onChange={(e) => setRemoveFor({ ...removeFor, reason: e.target.value })} placeholder="Compliance review / fraud / inactive" />
          <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold">
            Type "<b style={{ color: GOLD }}>{removeFor.username}</b>" to confirm
          </label>
          <input autoFocus className="ap-input w-full" value={removeFor.confirmText || ""} onChange={(e) => setRemoveFor({ ...removeFor, confirmText: e.target.value })} placeholder={removeFor.username} data-testid="remove-confirm-input" />
          <ModalActions onCancel={() => setRemoveFor(null)} onSave={removeAccount} saveLabel="Remove Permanently" accent="#ef4444" saveTestId="remove-confirm-btn" />
        </Modal>
      )}

      {/* Drill-down detail modal */}
      {detailFor && <ReferredPlayersModal affiliate={detailFor} onClose={() => setDetailFor(null)} />}
    </div>
  );
}

// ─────────────────────────── Modal helpers ───────────────────────────
function Modal({ children, onClose, accent = "#22c073" }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.78)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 560, padding: 24, borderRadius: 18, background: `linear-gradient(180deg, rgba(20,28,32,0.97), rgba(10,16,24,0.99))`, border: `1px solid ${accent}55`, boxShadow: `0 30px 80px ${accent}30` }}>
        {children}
      </div>
    </div>
  );
}

function ModalHead({ eyebrow, title, accent = "#22c073" }) {
  return (
    <>
      <div className="text-[10px] uppercase tracking-[0.28em] font-bold mb-1" style={{ color: accent }}>{eyebrow}</div>
      <h3 className="text-xl font-black text-white mb-2">{title}</h3>
    </>
  );
}

function ModalActions({ onCancel, onSave, saveLabel = "Save", accent = "#22c073", saveTestId }) {
  return (
    <div className="flex items-center gap-2 mt-5">
      <button onClick={onSave} className="px-4 h-10 rounded-xl font-bold text-[12px]" style={{ background: `linear-gradient(180deg, ${accent}, ${accent}99)`, color: "#06120a", boxShadow: `0 6px 20px ${accent}40` }} data-testid={saveTestId}>{saveLabel}</button>
      <button onClick={onCancel} className="px-4 h-10 rounded-xl font-bold text-[12px]" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", color: "#f5f1e6" }}>Cancel</button>
    </div>
  );
}

function AdminStat({ label, value, color, icon: Icon }) {
  return (
    <div className="rounded-xl p-4" style={{
      background: `radial-gradient(400px 120px at 0% 0%, ${color}18, transparent 60%), linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`,
      border: `1px solid ${color}3a`,
    }}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/55 font-bold">{label}</div>
        {Icon && (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18`, border: `1px solid ${color}55`, color }}>
            <Icon size={14} />
          </div>
        )}
      </div>
      <div className="text-[22px] font-black" style={{ color }}>{value}</div>
    </div>
  );
}

// ─────────────────────────── Referred Players Drill-down ───────────────────────────
function ReferredPlayersModal({ affiliate, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(15);
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/aff-accounts/${affiliate.user_id}/referred-players`, { params: { period_days: period } });
      setData(data);
    } catch (e) { toast.error("Failed to load drill-down"); }
    finally { setLoading(false); }
  }, [affiliate.user_id, period]);
  useEffect(() => { load(); }, [load]);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 70, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 1080, maxHeight: "90vh", overflow: "auto", padding: 24, borderRadius: 18, background: `linear-gradient(180deg, rgba(20,28,32,0.97), rgba(10,16,24,0.99))`, border: `1px solid ${ACCENT}55`, boxShadow: `0 30px 80px ${ACCENT}30` }}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold mb-1" style={{ color: ACCENT }}>REFERRED PLAYERS</div>
            <h3 className="text-xl font-black text-white">{affiliate.username} <span style={{ color: GOLD }}>· {affiliate.promo_code}</span></h3>
          </div>
          <div className="flex items-center gap-2">
            {[7, 15, 30, 90].map((d) => (
              <button key={d} onClick={() => setPeriod(d)} className="px-3 h-8 rounded-md text-[11px] font-bold" style={{ background: period === d ? `${ACCENT}28` : "rgba(255,255,255,0.04)", border: `1px solid ${period === d ? ACCENT : "rgba(255,255,255,0.1)"}`, color: period === d ? ACCENT : "#cbd5e1" }}>{d}d</button>
            ))}
            <button onClick={() => downloadFile(`${apiBase}/api/admin/aff-accounts/${affiliate.user_id}/detail.pdf?period_days=${period}`, `affiliate_${affiliate.username}.pdf`)} className="px-3 h-8 rounded-md text-[11px] font-bold inline-flex items-center gap-1" style={{ background: `${GOLD}1f`, border: `1px solid ${GOLD}66`, color: GOLD }}><FileDown size={11} /> PDF</button>
            <button onClick={onClose} className="p-2 rounded-md" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}><X size={16} className="text-white/70" /></button>
          </div>
        </div>
        {loading ? <div className="text-white/55 py-10 text-center">Loading…</div>
          : !data ? <div className="text-white/55 py-10 text-center">No data</div>
          : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                <Stat l="Players" v={data.totals.players} c={ACCENT} />
                <Stat l="Deposits ৳" v={fmt(data.totals.deposits)} c="#3b82f6" />
                <Stat l="Withdraws ৳" v={fmt(data.totals.withdrawals)} c="#f59e0b" />
                <Stat l="Net Loss ৳" v={fmt(data.totals.net_loss)} c={ACCENT} />
                <Stat l="Commission ৳" v={fmt(data.totals.commission)} c={GOLD} />
              </div>
              <div className="overflow-x-auto rounded-xl" style={{ border: `1px solid ${ACCENT}3a` }}>
                <table className="w-full text-[12.5px] min-w-[800px]">
                  <thead>
                    <tr className="text-left text-[10.5px] uppercase tracking-widest" style={{ color: `${ACCENT}cc`, background: `${NAVY}` }}>
                      <th className="py-2.5 px-3">Username</th>
                      <th className="py-2.5 px-3">Joined</th>
                      <th className="py-2.5 px-3 text-right">Deposits</th>
                      <th className="py-2.5 px-3 text-right">Withdraws</th>
                      <th className="py-2.5 px-3 text-right">Wins</th>
                      <th className="py-2.5 px-3 text-right">Losses</th>
                      <th className="py-2.5 px-3 text-right">Net Loss</th>
                      <th className="py-2.5 px-3 text-right">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.length === 0 && (
                      <tr><td colSpan="8" className="py-8 text-center text-white/55">No referred players in this period</td></tr>
                    )}
                    {data.rows.map((r) => (
                      <tr key={r.user_id} className="hover:bg-emerald-500/5 text-white" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                        <td className="py-2 px-3 font-bold">{r.username}</td>
                        <td className="py-2 px-3 text-white/65 text-[11px]">{(r.joined_at || "").slice(0, 10)}</td>
                        <td className="py-2 px-3 text-right">৳ {fmt(r.deposits)}</td>
                        <td className="py-2 px-3 text-right">৳ {fmt(r.withdrawals)}</td>
                        <td className="py-2 px-3 text-right" style={{ color: "#6ee7b7" }}>৳ {fmt(r.wins)}</td>
                        <td className="py-2 px-3 text-right" style={{ color: "#fca5a5" }}>৳ {fmt(r.losses)}</td>
                        <td className="py-2 px-3 text-right font-bold" style={{ color: ACCENT }}>৳ {fmt(r.net_loss)}</td>
                        <td className="py-2 px-3 text-right font-black" style={{ color: GOLD }}>৳ {fmt(r.commission)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
      </div>
    </div>
  );
}

function Stat({ l, v, c }) {
  return (
    <div className="rounded-lg p-3" style={{ background: `${c}10`, border: `1px solid ${c}33` }}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-bold">{l}</div>
      <div className="text-[16px] font-black mt-1" style={{ color: c }}>{v}</div>
    </div>
  );
}
